import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	type StatusCategory,
	TODO,
	IN_PROGRESS,
	DONE,
	categoryIdForStatusCategory,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { issueProgressDetails_versionProgress$key } from '@atlassian/jira-relay/src/__generated__/issueProgressDetails_versionProgress.graphql';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import type { WarningsCategory } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/types.tsx';
import {
	DONE_COLOR,
	IN_PROGRESS_COLOR,
	TODO_COLOR,
	WARNING_COLOR,
} from '../../common/constants.tsx';
import { messages } from './messages.tsx';

type Props = {
	jiraVersion: issueProgressDetails_versionProgress$key;
};

export const IssueProgressDetails = ({ jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showStatusCategoryIssues, showIssuesWithWarnings } = useFilterSortingQueryParams();

	const version = useFragment(
		graphql`
			fragment issueProgressDetails_versionProgress on JiraVersion {
				warningConfig {
					failingBuild
					openPullRequest
					openReview
					unreviewedCode
				}
				doneIssuesRedesignTemp: issues(filters: { statusCategories: [DONE] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				inProgressIssuesRedesignTemp: issues(filters: { statusCategories: [IN_PROGRESS] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				todoIssuesRedesignTemp: issues(filters: { statusCategories: [TODO] })
					@required(action: THROW) {
					totalCount @required(action: THROW)
				}
				warningsCount @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const {
		doneIssuesRedesignTemp: doneIssues,
		inProgressIssuesRedesignTemp: inProgressIssues,
		todoIssuesRedesignTemp: todoIssues,
	} = version;

	const { warningConfig } = version;
	const enabledWarningCategories: WarningsCategory[] = [];
	if (warningConfig?.failingBuild === 'ENABLED') enabledWarningCategories.push('FAILING_BUILD');
	if (warningConfig?.openPullRequest === 'ENABLED')
		enabledWarningCategories.push('OPEN_PULL_REQUEST');
	if (warningConfig?.openReview === 'ENABLED') enabledWarningCategories.push('OPEN_REVIEW');
	if (warningConfig?.unreviewedCode === 'ENABLED') enabledWarningCategories.push('UNREVIEWED_CODE');

	const messageFormatter = {
		b: (chunks: ReactNode[]) => <StatusCategoryName>{chunks}</StatusCategoryName>,
		s: (chunks: ReactNode[]) => <IssueCount>{chunks}</IssueCount>,
	};

	const createStatusClickHandler = (category: StatusCategory) => () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'progressDetailsShortcutFilter',
			{ filter: category },
		);
		const categoryId = String(categoryIdForStatusCategory(category));
		showStatusCategoryIssues([categoryId]);
	};

	return (
		<Container>
			<Row onClick={createStatusClickHandler(DONE)}>
				<StatusCategoryColorIndicator color={DONE_COLOR} />
				{formatMessage(messages.doneIssues, {
					...messageFormatter,
					count: doneIssues.totalCount,
				})}
			</Row>
			<Row onClick={createStatusClickHandler(IN_PROGRESS)}>
				<StatusCategoryColorIndicator color={IN_PROGRESS_COLOR} />
				{formatMessage(messages.inProgressIssues, {
					...messageFormatter,
					count: inProgressIssues.totalCount,
				})}
			</Row>
			<Row onClick={createStatusClickHandler(TODO)}>
				<StatusCategoryColorIndicator color={TODO_COLOR} />
				{formatMessage(messages.toDoIssues, {
					...messageFormatter,
					count: todoIssues.totalCount,
				})}
			</Row>
			{enabledWarningCategories.length > 0 && (
				<Row
					// Warning: this test ID is used by a Pollintor check
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="software-releases-version-detail-issue-list.ui.warning-filter-button"
					onClick={() => {
						showIssuesWithWarnings(enabledWarningCategories);
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'button',
							}),
							'progressBarShortcutFilter',
							{ filter: 'warnings' },
						);
					}}
				>
					<StatusCategoryColorIndicator color={WARNING_COLOR} />
					{formatMessage(messages.warningIssues, {
						...messageFormatter,
						count: version.warningsCount,
					})}
				</Row>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginTop: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusCategoryColorIndicator = styled.span<{ color: string }>({
	width: '10px',
	height: '10px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.color,

	borderRadius: '10px',
	marginRight: token('space.250'),
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Row = styled.div({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StatusCategoryName = styled.span({
	fontWeight: token('font.weight.semibold'),
	whiteSpace: 'pre',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueCount = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N300),
});
